import {
  Component,
  ElementRef,
  Renderer2,
  AfterViewInit,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  ViewEncapsulation,
  OnChanges,
  ChangeDetectionStrategy,
  Inject,
  LOCALE_ID,
} from '@angular/core';
import { Options, ColorAxisOptions, SeriesMapOptions } from 'highcharts';
import * as Highcharts from 'highcharts/highmaps';
import { merge, isObject, isArray } from 'lodash';
import { Observable } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';
import { BackendService, ColorService, FormatPipe, SessionService, DEPLOY_URL } from '@hub-components';
import {
  DEFAULT_TITLE_FONT_SIZE,
  DEFAULT_MAP_TYPE,
} from '../../../../constants';
import { WidgetStyle } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';

const HIGHCHARTS_OPTIONS: Partial<Options> = {
  chart: {
    animation: false,
    style: {
      color: '#949497',
      fontFamily: '"DIN2014-Light", sans-serif',
      fontSize: '14px',
    },
  },
  title: null,
  credits: {
    enabled: false,
  },
  mapNavigation: {
    enabled: false,
  },
  legend: {
    layout: 'horizontal',
    verticalAlign: 'bottom',
    borderWidth: 0,
    floating: true,
    align: 'center',
    enabled: true,
  },
  colorAxis: {
    tickLength: 0,
    stops: [
      [0, '#e0d9fc'],
      [0.5, '#7e63f7'],
      [1, '#2200ba'],
    ] as [number, string][],
    labels: {},
  } as ColorAxisOptions,
  series: [
    {
      type: 'map',
      states: {
        hover: {
          color: '#a4edba',
        },
      },
    } as SeriesMapOptions,
  ],
  tooltip: {
    borderWidth: 0,
    shadow: false,
    shared: true,
    useHTML: true,
  },
};

const MAPS: Record<string, Observable<any>> = {
};

@Component({
  selector: 'app-map',
  template: `
    <div class="map-wrapper" [class.has-footer]="!!footer">
      <div #containerRef class="map-container"></div>
      <div #footerRef *ngIf="!!footer" class="map-footer">
        <span *ngIf="footer.datasetInfo">{{footerData.datasetInfo}}</span>
        <span *ngIf="footer.dateRange">{{footerData.dateRange}}</span>
      </div>
    </div>
    `,
  styleUrls: ['map.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements AfterViewInit, OnChanges {
  @Input() series: any[];
  @Input() chartOptions: ChartOptions = {};
  @Input() style: WidgetStyle;
  @Input() datasetDisplayName: string;
  @Input() footer: any;
  @Input() footerData: any;

  @ViewChild('containerRef') containerRef: ElementRef;
  @ViewChildren('footerRef') footerRef: QueryList<any>;
  instance: any;
  window = <any>window;
  backgroundColor: string;

  formatter: any;

  constructor(
  @Inject(LOCALE_ID) locale: string,
    session: SessionService,
    @Inject(DEPLOY_URL) private deployUrl: string,
    private backendService: BackendService,
    private colorService: ColorService,
    private renderer: Renderer2,
  ) {
    this.formatter = new FormatPipe(locale, session);
  }

  ngAfterViewInit() {
    this.drawChart();
    $(this.window).resize(() => {
      setTimeout(() => {
        this.instance.reflow();
      });
    });
  }

  ngOnChanges() {
    if (this.instance) {
      this.drawChart();
    }
  }

  private drawChart() {
    this.loadMap().pipe(take(1)).subscribe(map => {
      const baseOptions: Partial<Options> = {
        colors: this.colorService.defaultColors,
        chart: { map },
      };
      let options = merge(baseOptions, HIGHCHARTS_OPTIONS, { deep: true }) as Options;
      this.setHighchartsOptionsFromChartOptions(options);
      this.setTooltipFormatter(options);
      this.setLegendFormatter(options);
      options = merge(options, { series: this.ensureNullValues(this.series) }, { deep: true }) as Options;
      this.instance = Highcharts.mapChart(this.containerRef.nativeElement, options);
    });
  }

  private loadMap(): Observable<any> {
    const name = this.chartOptions?.map?.mapType?.value || DEFAULT_MAP_TYPE.value;
    if (name in MAPS) {
      return MAPS[name];
    } else {
      return MAPS[name] = this.backendService.load(`${this.deployUrl || ''}maps/${name}.geo.json`)
        .pipe(
          shareReplay(),
        );
    }
  }

  private setHighchartsOptionsFromChartOptions(options) {
    const co = this.chartOptions;
    if (co) {
      if (this.footer?.datasetInfo || this.footer?.dateRange) {
        const borderWidth = this.style?.borderWidth || 0;
        const adjustedHeight = borderWidth > 0 ?
          `calc(90% - ${borderWidth * 2}px)` : '90%';
        this.renderer.setStyle(this.containerRef.nativeElement, 'height', adjustedHeight);
      } else {
        this.renderer.setStyle(this.containerRef.nativeElement, 'height', '100%');
      }

      if (co.colors) {
        options.colorAxis.stops[0] = [0, co.colors[0]];
        options.colorAxis.stops[1] = [0.5, co.colors[1]];
        options.colorAxis.stops[2] = [1, co.colors[2]];
      }

      if (co.backgroundColor) {
        options.chart.backgroundColor = co.backgroundColor;
        this.backgroundColor = co.backgroundColor;
      }

      if (co.title) {
        options.title = merge(options.title, {
          align: co.title.align || 'center',
          text: co.title.text,
          floating: false,
          style: {
            color: co.textColor,
            fontSize: (co.title.size || DEFAULT_TITLE_FONT_SIZE) + 'px',
            fontWeight: 'bold',
          },
        }, { deep: true });
      }

      if (co.legend) {
        options.legend = merge(options.legend, {
          enabled: (co.legend.layout || 'horizontal') !== 'disabled' && (co.legend['enabled'] !== undefined ? co.legend['enabled'] : true),
          align: co.legend.align || 'right',
          verticalAlign: co.legend.position || co.legend['legendPosition'] || 'bottom',
          layout: co.legend.layout || 'horizontal',
        }, { deep: true });
      }
    }
  }

  private setTooltipFormatter(options) {
    const mapComponent = this;
    options['tooltip']['formatter'] = function () {
      let html = '';
      html += `
      <div style="padding: 2px;">
        <span style="color:#666666; font-size:14px; font-weight:500;">${mapComponent.series[0].name}</span>
      </div>
      <div style="padding: 2px;">
        <span style="font-size:14px; font-weight:500;color:${this.color}">●</span> ${this.point.options.key}:&nbsp;&nbsp;
        <span font-size:14px; font-weight:500;></span>${mapComponent.formatter.transform(this.point.value, mapComponent.series[0].format)}
      </div>
      `;
      return html;
    };
  }

  private setLegendFormatter(options) {
    const mapComponent = this;
    options['colorAxis']['labels']['formatter'] = function () {
      if (mapComponent.series[0].format && mapComponent.series[0].format === 'percent') {
        return mapComponent.formatter.transform(this.value, 'percent');
      } else return this.axis.defaultLabelFormatter.call(this);
    };
  }

  // Highcharts will not render date series properly if values are undefined
  private ensureNullValues(series) {
    const newSeries = series.map(s => {
      const filteredData = s.data.filter(d => d.value > 0);
      const newData = filteredData.map(d => {
        if (isObject(d)) {
          return merge(d, { y: d.y || 0 });
        } else if (isArray(d)) {
          return d.map(_d => _d || 0);
        } else {
          return d || 0;
        }
      });
      return merge(s, { data: newData });
    });
    return newSeries;
  }
}
